import { useCapabilityContext } from "./CapabilityProvider";

/**
 * useCapability — the canonical entry point for any visibility decision.
 *
 *   const { map, loading, hasModule, getModule, refresh } = useCapability();
 *
 * Returns the whole capability map plus convenience helpers. Components
 * that need only a single primitive should prefer the more specific hooks
 * below — they re-render only when their slice changes.
 */
export const useCapability = () => useCapabilityContext();

/**
 * useHasModule — boolean predicate for L1 frontend rendering enforcement.
 *
 *   const showSurveys = useHasModule("surveys");
 *
 * Honors L9 (hidden by default) by returning false when the map is empty
 * or still loading.
 */
export const useHasModule = (moduleName) => {
    const { hasModule } = useCapabilityContext();
    return hasModule(moduleName);
};

/**
 * useScope — read the scope primitive (§2.2 col 'scope').
 *
 * Used by data-bound components that paginate or filter by org/region/location.
 * Returning the scope here lets components reason about hierarchy without
 * re-implementing org-anchor logic per page.
 */
export const useScope = () => {
    const { map } = useCapabilityContext();
    return map?.scope || { organization_ids: [], region_ids: [], location_ids: [], card_ids: [], unbounded: false };
};

/**
 * useGovernance — read aggregate verbs (§2.2 col 'governance').
 *
 * UI elements that gate destructive actions (lock, override, propagate,
 * break-glass) consult this single hook. It is intentionally NOT a 200-row
 * permission matrix (§2.7).
 */
export const useGovernance = () => {
    const { map } = useCapabilityContext();
    return map?.governance || { read: false, write: false, lock: false, override: false, propagate: false, break_glass: false };
};
